/**
 * Conexión Web Bluetooth con la impresora térmica.
 *
 * Verificado el 27-ago-2026 contra la Jolimark TP510 del club (módulo ISSC BT5050),
 * desde Bluefy en el iPad. De esa sesión salen todas las constantes de aquí:
 *
 *  - Imprimen DOS canales: `FF00`/`FF02` y el UART transparente de Microchip.
 *    Se prueban en ese orden y el que funcione se guarda en el emparejamiento.
 *  - La impresora CONTESTA al comando de estado, así que sabemos si le falta papel
 *    antes de mandarle un ticket, en vez de enterarnos cuando no sale.
 *  - Se duerme sola a los 30-40 s sin tráfico. El latido cada 15 s lo evita:
 *    seis minutos seguidos sin una caída en la prueba.
 */
import { leerEmparejamiento } from "./emparejamiento";

/** Servicios a declarar en `requestDevice`: lo que no se pida aquí, luego no se puede ni ver. */
export const SERVICIOS_CONOCIDOS = [
  "0000ff00-0000-1000-8000-00805f9b34fb", // Jolimark TP510 — el que imprime
  "49535343-fe7d-4ae5-8fa9-9fafd205e455", // UART transparente ISSC/Microchip — también imprime
  "0000ff80-0000-1000-8000-00805f9b34fb",
  "000018f0-0000-1000-8000-00805f9b34fb", // habitual en otras térmicas chinas
  "0000ffe0-0000-1000-8000-00805f9b34fb", // módulos HM-10
  "0000fff0-0000-1000-8000-00805f9b34fb",
  "0000ae30-0000-1000-8000-00805f9b34fb",
  "6e400001-b5a3-f393-e0a9-e50e24dcca9e", // Nordic UART
];

/** Paquete BLE sin MTU negociado. Subirlo se come bytes del ticket. */
const TROZO = 20;
const PAUSA_MS = 20;
const LATIDO_MS = 15000;

/** DLE EOT 1: el único comando ESC/POS que obliga a la impresora a contestar. */
const SONDA_ESTADO = new Uint8Array([0x10, 0x04, 0x01]);

const esperar = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

/** Plazo para engancharse una vez elegida la impresora. NO cubre el selector del
 *  sistema: ahí manda el usuario y puede tardar lo que quiera. */
export const PLAZO_CONEXION_MS = 20000;

/**
 * Plazo para que la impresora conteste tras un ticket.
 *
 * Generoso a propósito: una máquina OCUPADA imprimiendo tarda bastante más en
 * contestar que una parada, y con 3 s dábamos por fallido un ticket que sí iba a
 * salir. Apretar esta tuerca de más rompe justo lo que venía a proteger.
 */
export const PLAZO_ACUSE_MS = 6000;

/**
 * Respiro tras mandar un ticket antes de preguntarle nada.
 *
 * Estas térmicas tienen un buffer minúsculo: si le encadenas el siguiente trabajo
 * sin dejarla terminar, lo acepta, contesta que está viva y lo tira. Visto en real
 * el 28-ago vaciando una cola de dos: salió el primero y el segundo se perdió,
 * marcado como impreso.
 */
export const RESPIRO_MS = 1200;

/**
 * Web Bluetooth no pone plazo a `connect`: si la impresora está apagada, dormida o
 * COGIDA POR OTRO APARATO (estos módulos aceptan una sola conexión), la promesa se
 * queda colgada para siempre y la pantalla se queda en "Conectando…" sin explicar
 * nada. Verificado en real el 27-ago desde el Mac con el iPad aún enganchado.
 */
function conPlazo(promesa, ms = PLAZO_CONEXION_MS) {
  let reloj;
  const plazo = new Promise((_, rechazar) => {
    reloj = setTimeout(() => rechazar(new Error(
      "La impresora no responde. Comprueba que está encendida y que no la tenga " +
      "cogida otro dispositivo: solo admite una conexión a la vez."
    )), ms);
  });
  return Promise.race([promesa, plazo]).finally(() => clearTimeout(reloj));
}

/**
 * Descifra la respuesta a DLE EOT 1. Devuelve `null` si el byte no es una respuesta
 * de estado — estas impresoras sueltan también notificaciones del módulo Bluetooth
 * que no tienen nada que ver.
 */
export function interpretarEstado(byte) {
  if ((byte & 0x93) !== 0x12) return null;
  return {
    offline: Boolean(byte & 0x08),
    tapaAbierta: Boolean(byte & 0x20),
    sinPapel: Boolean(byte & 0x40),
  };
}

function primeraEscribible(caracteristicas) {
  return caracteristicas.find((c) => c.properties.write || c.properties.writeWithoutResponse) || null;
}

export function crearImpresoraBLE({ onEstado, onDesconexion } = {}) {
  // Mutables y no parámetros fijos: la conexión sobrevive a que la pantalla se monte y
  // se desmonte, y cada montaje vuelve a apuntar los avisos a SU estado de React.
  let avisoEstado = onEstado;
  let avisoDesconexion = onDesconexion;
  let dispositivo = null;
  let escritura = null;
  let servicioUsado = "";
  let latido = null;
  let papel = null;
  /**
   * Una sola operación GATT a la vez.
   *
   * Web Bluetooth lanza "GATT operation already in progress" si se solapan dos
   * escrituras sobre la misma característica, y aquí se solapan solas: el latido
   * cada 15 s, la sonda al conectar y el ticket compiten por el mismo canal. Sin
   * esta cola, conectar y vaciar la cola a la vez tumbaba el ticket — visto el
   * 28-ago: la comanda se quedaba encolada y no salía papel.
   */
  let cadena = Promise.resolve();
  /** ¿La impresora tiene canal de respuesta? Sin él no se puede confirmar nada. */
  let hayCanalEstado = false;
  /** Resolvedor de la confirmación en curso, si estamos esperando un acuse. */
  let esperandoAcuse = null;

  function enCadena(tarea) {
    const resultado = cadena.then(() => tarea());
    cadena = resultado.then(() => {}, () => {});
    return resultado;
  }

  const avisarEstado = () => { if (avisoEstado) avisoEstado(papel); };

  function pararLatido() {
    if (latido) clearInterval(latido);
    latido = null;
  }

  function arrancarLatido() {
    pararLatido();
    latido = setInterval(() => {
      // Si falla no hay nada que hacer: el evento de desconexión ya avisa a la UI.
      escribirCrudo(SONDA_ESTADO).catch(() => {});
    }, LATIDO_MS);
  }

  /**
   * CON respuesta siempre que la característica lo permita.
   *
   * La escritura SIN respuesta no falla nunca: con la impresora apagada devuelve
   * "correcto" igual, y entonces damos el ticket por impreso, lo sacamos de la cola
   * y lo apuntamos. Sin papel, sin aviso y sin forma de saberlo — una comanda que
   * nadie prepara y que nadie echa en falta. Visto en real el 28-ago.
   *
   * Con respuesta, el navegador espera el acuse del periférico y lanza error si no
   * llega. Es algo más lento y es exactamente lo que queremos: aquí la certeza vale
   * más que los milisegundos.
   */
  function escribirCrudo(bytes) {
    return enCadena(async () => {
      if (!escritura) throw new Error("No hay impresora conectada");
      const conAcuse = escritura.properties.write && escritura.writeValue;
      for (let i = 0; i < bytes.length; i += TROZO) {
        const trozo = bytes.slice(i, i + TROZO);
        if (conAcuse) await escritura.writeValue(trozo);
        else await escritura.writeValueWithoutResponse(trozo);
        if (PAUSA_MS) await esperar(PAUSA_MS);
      }
    });
  }

  /**
   * Fuera del bucle de descubrimiento a propósito: declarándolo dentro, cada vuelta
   * creaba un cierre nuevo sobre `papel` (`no-loop-func`) y el CI lo rechaza.
   */
  function alLlegarEstado(ev) {
    const datos = new Uint8Array(ev.target.value.buffer);
    if (!datos.length) return;
    const leido = interpretarEstado(datos[0]);
    if (leido) { papel = leido; avisarEstado(); }
    // Cualquier respuesta vale como prueba de vida: si contesta, está encendida.
    if (esperandoAcuse) esperandoAcuse();
  }

  /** El canal de estado vive en el mismo servicio que el de escritura. */
  async function escucharEstado(caracteristicas) {
    const escucha = caracteristicas.find((c) => c.properties.notify);
    if (!escucha) return;
    try {
      escucha.removeEventListener("characteristicvaluechanged", alLlegarEstado);
      escucha.addEventListener("characteristicvaluechanged", alLlegarEstado);
      await escucha.startNotifications();
      hayCanalEstado = true;
    } catch {
      // Sin canal de estado se imprime igual: solo perdemos el aviso de "sin papel".
    }
  }

  /**
   * Que la impresora CONTESTE, que es lo único que demuestra que imprimió.
   *
   * Una escritura con acuse solo prueba que el módulo Bluetooth recibió los bytes.
   * Con la máquina apagada el módulo sigue vivo unos segundos, los acepta, los
   * confirma y los tira. Dábamos el ticket por impreso, lo sacábamos de la cola y
   * lo apuntábamos: una comanda perdida que nadie echa en falta. Visto en real el
   * 28-ago, con la impresora apagada delante.
   */
  function confirmarViva(ms = PLAZO_ACUSE_MS) {
    if (!hayCanalEstado) return Promise.resolve(true); // sin canal no se puede exigir
    return new Promise((resolve) => {
      let resuelto = false;
      const reloj = setTimeout(() => {
        if (resuelto) return;
        resuelto = true; esperandoAcuse = null; resolve(false);
      }, ms);
      esperandoAcuse = () => {
        if (resuelto) return;
        resuelto = true; clearTimeout(reloj); esperandoAcuse = null; resolve(true);
      };
      escribirCrudo(SONDA_ESTADO).catch(() => {});
    });
  }

  function alDesconectar() {
    escritura = null;
    servicioUsado = "";
    papel = null;
    hayCanalEstado = false;
    if (esperandoAcuse) esperandoAcuse = null;
    pararLatido();
    if (avisoDesconexion) avisoDesconexion();
  }

  /** Busca el canal que imprime y se suscribe al de estado, en el mismo servicio. */
  async function preparar(servidor, preferido) {
    const servicios = await servidor.getPrimaryServices();
    const ordenados = [...servicios].sort((a, b) => {
      // El que ya sabemos que funciona en ESTE aparato va primero.
      if (preferido) {
        if (a.uuid === preferido) return -1;
        if (b.uuid === preferido) return 1;
      }
      return SERVICIOS_CONOCIDOS.indexOf(a.uuid) - SERVICIOS_CONOCIDOS.indexOf(b.uuid);
    });

    for (const servicio of ordenados) {
      let caracteristicas = [];
      try {
        caracteristicas = await servicio.getCharacteristics();
      } catch {
        continue; // servicio ilegible: siguiente
      }
      const candidata = primeraEscribible(caracteristicas);
      if (!candidata) continue;

      escritura = candidata;
      servicioUsado = servicio.uuid;
      await escucharEstado(caracteristicas);
      return true;
    }
    return false;
  }

  async function enganchar(elegido, preferido) {
    dispositivo = elegido;
    dispositivo.removeEventListener("gattserverdisconnected", alDesconectar);
    dispositivo.addEventListener("gattserverdisconnected", alDesconectar);

    let servidor;
    let listo = false;
    try {
      servidor = await conPlazo(dispositivo.gatt.connect());
      listo = await conPlazo(preparar(servidor, preferido));
    } catch (e) {
      // Dejar el intento a medias mantiene ocupada la impresora para el siguiente.
      try { dispositivo.gatt.disconnect(); } catch { /* ya estaba fuera */ }
      throw e;
    }
    if (!listo) {
      dispositivo.gatt.disconnect();
      throw new Error("La impresora no expone ningún canal de escritura");
    }

    arrancarLatido();
    escribirCrudo(SONDA_ESTADO).catch(() => {});

    return {
      deviceId: dispositivo.id,
      deviceName: dispositivo.name || "",
      servicio: servicioUsado,
      caracteristica: escritura.uuid,
    };
  }

  return {
    disponible: true,

    /** Reapunta los avisos al montaje actual de la pantalla. */
    observar({ onEstado: est, onDesconexion: des } = {}) {
      avisoEstado = est;
      avisoDesconexion = des;
    },

    conectada: () => Boolean(dispositivo && dispositivo.gatt && dispositivo.gatt.connected && escritura),

    estadoPapel: () => papel,

    /** Abre el selector del sistema. Obliga a un gesto humano: va SIEMPRE colgado de un botón. */
    async conectar(venueId) {
      const guardado = leerEmparejamiento(venueId);
      const elegido = await navigator.bluetooth.requestDevice({
        acceptAllDevices: true,
        optionalServices: SERVICIOS_CONOCIDOS,
      });
      return enganchar(elegido, guardado?.servicio);
    },

    /**
     * Reconexión sin selector, para el aparato ya emparejado.
     *
     * En Bluefy esto HOY no funciona (lista el dispositivo pero `connect` falla con
     * un error vacío, incluso pulsando un botón), así que la UI tiene que estar
     * preparada para caer al selector. Se intenta igualmente porque en Chrome sí va
     * y porque el día que Bluefy lo arregle, deja de haber toque diario.
     */
    async reconectar(venueId) {
      const guardado = leerEmparejamiento(venueId);
      if (!guardado || !navigator.bluetooth.getDevices) return null;
      const conocidos = await navigator.bluetooth.getDevices();
      const elegido = conocidos.find((d) => d.id === guardado.deviceId);
      if (!elegido) return null;
      return enganchar(elegido, guardado.servicio);
    },

    async imprimir(bytes) {
      await escribirCrudo(bytes);
      // Dejar que termine ANTES de preguntarle: si se le pregunta a mitad de
      // trabajo, contesta que vive y no dice nada del ticket.
      await esperar(RESPIRO_MS);
      if (!(await confirmarViva())) {
        throw new Error(
          "La impresora recibió el ticket pero no contesta. Puede estar apagada, " +
          "sin papel o con la tapa abierta: el ticket se queda en la cola."
        );
      }
    },

    desconectar() {
      pararLatido();
      if (dispositivo && dispositivo.gatt && dispositivo.gatt.connected) dispositivo.gatt.disconnect();
      dispositivo = null;
      escritura = null;
      papel = null;
    },
  };
}
